import {FC} from "react"
import {ShapeType, ShapeWidgetData} from "@dv5/domain"
import {getShapeStrokeProps} from "./shapeLineStyle"

interface ShapeProps {
    data: ShapeWidgetData
    width: number
    height: number
}

export const ShapeRect: FC<ShapeProps> = ({ data, width, height }) => {
    const bw = data.borderWidth != null ? data.borderWidth : 1
    return (
        <rect
            x={bw}
            y={bw}
            width={width - bw * 2}
            height={height - bw * 2}
            rx={0}
            ry={0}
            fill={data.bgColor}
            stroke={data.borderColor}
            strokeWidth={bw}
            {...getShapeStrokeProps(data.lineStyle, bw)}
        />
    )
}

export const ShapeRoundedRect: FC<ShapeProps> = ({ data, width, height }) => {
    const bw = data.borderWidth != null ? data.borderWidth : 1
    const w = width - bw * 2
    const h = height - bw * 2
    const r = Math.min(w, h) * 0.15
    return (
        <rect
            x={bw}
            y={bw}
            width={w}
            height={h}
            rx={r}
            ry={r}
            fill={data.bgColor}
            stroke={data.borderColor}
            strokeWidth={bw}
            {...getShapeStrokeProps(data.lineStyle, bw)}
        />
    )
}

export const ShapeOval: FC<ShapeProps> = ({ data, width, height }) => {
    const bw = data.borderWidth != null ? data.borderWidth : 1
    const w = width - bw * 2
    const h = height - bw * 2
    return (
        <ellipse
            cx={width / 2}
            cy={height / 2}
            rx={Math.max(0, w / 2)}
            ry={Math.max(0, h / 2)}
            fill={data.bgColor}
            stroke={data.borderColor}
            strokeWidth={bw}
            {...getShapeStrokeProps(data.lineStyle, bw)}
        />
    )
}

export const ShapeTriangle: FC<ShapeProps> = ({ data, width, height }) => {
    const bw = data.borderWidth != null ? data.borderWidth : 1
    const cy = height / 2
    const path = `M${width / 2} ${bw} L${width - bw} ${height - bw} L${bw} ${height - bw} Z`
    return (
        <path d={path}
              fill={data.bgColor}
              stroke={data.borderColor}
              strokeWidth={bw}
              {...getShapeStrokeProps(data.lineStyle, bw)}
              strokeLinejoin='round'
        />
    )
}

export const ShapeDiamond: FC<ShapeProps> = ({ data, width, height }) => {
    const bw = data.borderWidth != null ? data.borderWidth : 1
    const cx = width / 2
    const cy = height / 2
    const path = `M${cx} ${bw} L${width - bw} ${cy} L${cx} ${height - bw} L${bw} ${cy} Z`
    return (
        <path d={path}
              fill={data.bgColor}
              stroke={data.borderColor}
              strokeWidth={bw}
              {...getShapeStrokeProps(data.lineStyle, bw)}
              strokeLinejoin='round'
        />
    )
}

export const ShapeDatabase: FC<ShapeProps> = ({ data, width, height }) => {
    const bw = data.borderWidth != null ? data.borderWidth : 1
    const w = width - bw * 2
    const h = height - bw * 2
    const cx = width / 2
    const topY = bw + h * 0.25
    const botY = bw + h * 0.75
    const rx = Math.max(0, w / 2)
    const ry = Math.max(0, h * 0.2)

    return (
        <g>
            <path d={`M ${bw} ${topY} L ${bw} ${botY} A ${rx} ${ry} 0 0 0 ${width - bw} ${botY} L ${width - bw} ${topY} Z`}
                  fill={data.bgColor}
                  stroke={data.borderColor}
                  strokeWidth={bw}
                  {...getShapeStrokeProps(data.lineStyle, bw)}
                  strokeLinejoin='round'
            />
            <ellipse cx={cx} cy={topY} rx={rx} ry={ry}
                     fill={data.bgColor}
                     stroke={data.borderColor}
                     strokeWidth={bw}
                     {...getShapeStrokeProps(data.lineStyle, bw)}
            />
        </g>
    )
}

export const ShapeQueue: FC<ShapeProps> = ({ data, width, height }) => {
    const bw = data.borderWidth != null ? data.borderWidth : 1
    const w = width - bw * 2
    const h = height - bw * 2
    const r = Math.min(w, h) * 0.4
    return (
        <rect
            x={bw}
            y={bw}
            width={w}
            height={h}
            rx={r}
            ry={r}
            fill={data.bgColor}
            stroke={data.borderColor}
            strokeWidth={bw}
            {...getShapeStrokeProps(data.lineStyle, bw)}
        />
    )
}

export const ShapeProcess: FC<ShapeProps> = ({ data, width, height }) => {
    const bw = data.borderWidth != null ? data.borderWidth : 1
    const w = width - bw * 2
    const h = height - bw * 2
    const cx = width / 2
    const cy = height / 2
    const inset = w * 0.15
    const path = `M ${bw + inset} ${bw} L ${width - bw - inset} ${bw} L ${width - bw} ${cy} L ${width - bw - inset} ${height - bw} L ${bw + inset} ${height - bw} L ${bw} ${cy} Z`
    return (
        <path d={path}
              fill={data.bgColor}
              stroke={data.borderColor}
              strokeWidth={bw}
              {...getShapeStrokeProps(data.lineStyle, bw)}
              strokeLinejoin='round'
        />
    )
}

export const shapeComponents: Record<ShapeType, FC<ShapeProps>> = {
    [ShapeType.RECTANGLE]: ShapeRect,
    [ShapeType.ROUNDED_RECTANGLE]: ShapeRoundedRect,
    [ShapeType.OVAL]: ShapeOval,
    [ShapeType.TRIANGLE]: ShapeTriangle,
    [ShapeType.DIAMOND]: ShapeDiamond,
    [ShapeType.DATABASE]: ShapeDatabase,
    [ShapeType.QUEUE]: ShapeQueue,
    [ShapeType.PROCESS]: ShapeProcess,
}
